// app/src/Pay.jsx — pay schedule (paydays only; no amounts/payroll data).
//
// Members see WHEN they get paid (pay-period calendar + payday). Managers add
// one-off periods or a recurring schedule (weekly / every 2 weeks / twice a month
// / monthly) that repeats automatically. Kanjo Crew stores no wages/hours/amounts.

// Localized cadence label, e.g. freqLabel('biweekly') → "Every 2 weeks".
function freqLabel(f) { return t('freq.' + f, f); }

function PayScreen() {
  const wid = window.crew && window.crew.state.activeWorkplaceId;
  const me = (window.crew && window.crew.state.me) || { workplaces: [] };
  const myWp = (me.workplaces || []).find((w) => w.workplaceId === wid) || {};
  const isManager = myWp.role === 'owner' || myWp.role === 'manager';

  const [periods, setPeriods] = React.useState(null);
  const [recurrence, setRecurrence] = React.useState(null);
  const [adding, setAdding] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!wid) return;
    try {
      const r = await window.crew.api(`/workplaces/${wid}/pay-schedule`, { workplaceId: wid });
      setPeriods((r && r.periods) || []);
      setRecurrence((r && r.recurrence) || null);
    } catch (e) { console.warn('[pay] load', e && e.message); setPeriods([]); setRecurrence(null); }
  }, [wid]);
  React.useEffect(() => { load(); }, [load]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const next = (periods || []).find((p) => p.payday >= todayStr);

  const remove = async (p) => {
    if (!window.confirm(t('pay.removeConfirm', 'Remove the payday {date}?', { date: p.payday }))) return;
    try { await window.crew.api(`/workplaces/${wid}/pay-schedule/${encodeURIComponent(p.periodId)}`, { method: 'DELETE', workplaceId: wid }); await load(); }
    catch (e) { console.warn('[pay] remove', e && e.message); }
  };

  const removeRecurrence = async () => {
    if (!window.confirm(t('pay.stopConfirm', 'Stop the repeating pay schedule? Upcoming paydays will no longer be generated.'))) return;
    try { await window.crew.api(`/workplaces/${wid}/pay-schedule/recurrence`, { method: 'DELETE', workplaceId: wid }); await load(); }
    catch (e) { console.warn('[pay] remove recurrence', e && e.message); }
  };

  return (
    <KShell tab="pay">
      <KHeader title={t('nav.pay', 'Pay')} />
      <div style={{ padding: '12px 16px' }}>
        <div style={{ fontSize: 12.5, color: 'var(--on-soft)', marginBottom: 12 }}>
          {t('pay.subtitle', "Your pay schedule — when paydays land. (Kanjo Crew doesn't store amounts.)")}
        </div>

        {next && (
          <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--primary)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--primary)', letterSpacing: '.4px' }}>{t('pay.nextPayday', 'NEXT PAYDAY')}</div>
            <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 20, color: 'var(--ink)', margin: '4px 0' }}>{fmtDate(next.payday)}</div>
            {next.label && <div style={{ fontSize: 13, color: 'var(--sub)' }}>{next.label}</div>}
            {(next.periodStart && next.periodEnd) && <div style={{ fontSize: 12.5, color: 'var(--sub)' }}>{t('pay.period', 'Period')}: {fmtDate(next.periodStart)} – {fmtDate(next.periodEnd)}</div>}
          </div>
        )}

        {recurrence && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 18 }}>🔁</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: 'var(--ink)', fontSize: 14 }}>{t('pay.repeatsLabel', 'Repeats {freq}', { freq: freqLabel(recurrence.frequency).toLowerCase() })}</div>
              <div style={{ fontSize: 12, color: 'var(--sub)' }}>{t('pay.since', 'Since {date}', { date: fmtDate(recurrence.anchorPayday) })}{recurrence.label ? ` · ${recurrence.label}` : ''}</div>
            </div>
            {isManager && <button onClick={removeRecurrence} title={t('pay.repeats', 'REPEATS')} style={{ border: 'none', background: 'none', color: 'var(--sub)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>}
          </div>
        )}

        {isManager && <button onClick={() => setAdding(true)} style={dashBtn2}>{t('pay.addPeriod', '+ Add pay period')}</button>}

        {periods == null && <div style={{ color: 'var(--on-dim)', padding: 12 }}>…</div>}
        {periods != null && periods.length === 0 && <div style={{ color: 'var(--on-soft)', textAlign: 'center', padding: 20 }}>{t('pay.none', 'No pay schedule yet.')}</div>}
        {(periods || []).map((p) => (
          <div key={p.periodId} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: 'var(--ink)', fontSize: 14 }}>{t('pay.paidOn', 'Paid {date}', { date: fmtDate(p.payday) })}</div>
              {(p.periodStart && p.periodEnd) && <div style={{ fontSize: 12.5, color: 'var(--sub)' }}>{fmtDate(p.periodStart)} – {fmtDate(p.periodEnd)}</div>}
              {p.label && <div style={{ fontSize: 12, color: 'var(--sub)' }}>{p.label}</div>}
            </div>
            {p.recurring && <span style={{ fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: 'var(--primary-soft)', color: 'var(--primary)' }}>{t('pay.repeats', 'REPEATS')}</span>}
            {p.payday < todayStr && <span style={{ fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: 'var(--surface-2)', color: 'var(--sub)' }}>{t('pay.paid', 'PAID')}</span>}
            {isManager && !p.recurring && <button onClick={() => remove(p)} title="Remove" style={{ border: 'none', background: 'none', color: 'var(--sub)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>}
          </div>
        ))}
      </div>
      {adding && <AddPeriod wid={wid} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
    </KShell>
  );
}

function AddPeriod({ wid, onClose, onDone }) {
  const [frequency, setFrequency] = React.useState(''); // '' = one-time
  const [periodStart, setStart] = React.useState('');
  const [periodEnd, setEnd] = React.useState('');
  const [payday, setPayday] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const recurring = !!frequency;
  const submit = async () => {
    if (!payday || busy) return;
    setBusy(true);
    try {
      const body = { payday, periodStart, periodEnd, label };
      if (frequency) body.frequency = frequency;
      await window.crew.api(`/workplaces/${wid}/pay-schedule`, { method: 'POST', workplaceId: wid, body });
      onDone();
    } catch (e) { console.warn(e); setBusy(false); }
  };
  const inp = { width: '100%', padding: '11px 12px', marginTop: 6, marginBottom: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 15, boxSizing: 'border-box' };
  const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--sub)' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,12,8,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 430, background: 'var(--surface)', borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 20 }}>
        <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 18, color: 'var(--ink)', marginBottom: 14 }}>{t('pay.title', 'Add pay period')}</div>
        <div style={lbl}>{t('pay.repeat', 'Repeat')}</div>
        <select value={frequency} onChange={(e) => setFrequency(e.target.value)} style={inp}>
          <option value="">{t('freq.oneTime', "One-time (doesn't repeat)")}</option>
          <option value="weekly">{t('freq.weekly', 'Weekly')}</option>
          <option value="biweekly">{t('freq.biweekly', 'Every 2 weeks')}</option>
          <option value="semimonthly">{t('freq.semimonthly', 'Twice a month')}</option>
          <option value="monthly">{t('freq.monthly', 'Monthly')}</option>
        </select>
        <div style={lbl}>{recurring ? t('pay.firstPayday', 'First payday') : t('pay.payday', 'Payday')}</div><input type="date" value={payday} onChange={(e) => setPayday(e.target.value)} style={inp} />
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}><div style={lbl}>{recurring ? t('pay.firstPeriodStart', 'First period start') : t('pay.periodStart', 'Period start')}</div><input type="date" value={periodStart} onChange={(e) => setStart(e.target.value)} style={inp} /></div>
          <div style={{ flex: 1 }}><div style={lbl}>{recurring ? t('pay.firstPeriodEnd', 'First period end') : t('pay.periodEnd', 'Period end')}</div><input type="date" value={periodEnd} onChange={(e) => setEnd(e.target.value)} style={inp} /></div>
        </div>
        {recurring && <div style={{ fontSize: 11.5, color: 'var(--sub)', marginTop: -4, marginBottom: 10 }}>{t('pay.repeatHint', 'Paydays will repeat {freq} from the first payday.', { freq: freqLabel(frequency).toLowerCase() })}</div>}
        <div style={lbl}>{t('pay.labelField', 'Label (optional)')}</div><input value={label} onChange={(e) => setLabel(e.target.value)} style={inp} />
        <button onClick={submit} disabled={!payday || busy} style={{ width: '100%', padding: 13, border: 'none', borderRadius: 'var(--radius-sm)', background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: payday && !busy ? 1 : 0.6 }}>{busy ? '…' : (recurring ? t('pay.saveRepeating', 'Save repeating schedule') : t('common.add', 'Add'))}</button>
        <button onClick={onClose} style={{ width: '100%', marginTop: 8, padding: 10, border: 'none', background: 'none', color: 'var(--sub)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('common.cancel', 'Cancel')}</button>
      </div>
    </div>
  );
}

function fmtDate(d) { try { return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; } }
const dashBtn2 = { width: '100%', padding: 11, marginBottom: 12, border: '1px dashed var(--line)', background: 'var(--surface)', borderRadius: 'var(--radius-sm)', color: 'var(--primary)', fontWeight: 700, fontSize: 14, cursor: 'pointer' };

Object.assign(window, { PayScreen });
